import React, { createContext, useContext, useMemo } from "react";

import {
  AvailableIntegrationFragment,
  IntegrationAbility,
  useAvailableIntegrationsQuery,
  useDestinationIntegrationsQuery,
} from "@/apollo/types";
import {
  bootStageTimings,
  isBootStageMarked,
  markBootStage,
} from "@/monitoring/bootMarks";
import { bootVisibility } from "@/monitoring/bootVisibility";
import {
  launchDarklyIdentifyCount,
  launchDarklyIdentifySkipped,
} from "@/features/feature-flags/identifyCount";
import { useMixpanel } from "@/monitoring/mixpanel";
import { useCurrentAccountIdValue } from "@/providers/account";

import {
  readIntegrationsSnapshot,
  writeIntegrationsSnapshot,
} from "./integrationsSnapshot";
import { hasAbility } from "./utils";

export const IntegrationContext = createContext<
  AvailableIntegrationFragment[] | null
>(null);

export const useIntegrations = (
  ability?: IntegrationAbility,
): AvailableIntegrationFragment[] => {
  const integrations = useContext(IntegrationContext);
  if (integrations === null) {
    throw new Error(
      "useIntegrations must be used within a IntegrationsProvider",
    );
  }
  return useMemo(() => {
    const list =
      ability === undefined
        ? integrations
        : integrations.filter((i) => hasAbility(i, ability));
    return list;
  }, [integrations, ability]);
};

export const useIntegrationsMap = (
  ability?: IntegrationAbility,
): Map<string, AvailableIntegrationFragment> => {
  const integrations = useIntegrations(ability);
  return useMemo(() => {
    return new Map(integrations.map((x) => [x.id, x]));
  }, [integrations]);
};

export function useIntegration(integrationId: string | undefined | null) {
  const integrations = useIntegrationsMap();
  if (integrationId == null) {
    return null;
  }
  return integrations.get(integrationId) ?? null;
}

const IntegrationsStaleContext = createContext(false);

/**
 * True while `useIntegrations()` is serving a persisted snapshot rather than a
 * response. The values are a previous load's, so anything that acts
 * irreversibly on a missing integration — reporting it, redirecting away —
 * should wait for this to turn false.
 */
export function useIntegrationsAreStale() {
  return useContext(IntegrationsStaleContext);
}

export const IntegrationsProvider = ({ children }: React.PropsWithChildren) => {
  const accountId = useCurrentAccountIdValue();
  const mixpanel = useMixpanel();
  const { data, loading } = useAvailableIntegrationsQuery();
  const fresh = data?.availableIntegrations;

  const snapshot = useMemo(
    () => (fresh ? null : readIntegrationsSnapshot(accountId)),
    [fresh, accountId],
  );

  React.useEffect(() => {
    if (!fresh || !accountId) return;
    writeIntegrationsSnapshot(accountId, fresh);
  }, [fresh, accountId]);

  const integrations = fresh ?? snapshot;

  React.useEffect(() => {
    // Once per page load: a remount after in-app navigation is not a boot.
    if (!integrations || isBootStageMarked("integrations-ready")) return;
    markBootStage("integrations-ready");
    // Deliberately the moment this provider unblocks, not the moment the user
    // has their data: the page chunk and its queries still follow. A narrow
    // measure isolates the effect of the snapshot instead of burying it under
    // everything downstream.
    mixpanel.track("Workspace Integrations Ready", {
      ms_since_page_load: Math.round(performance.now()),
      integrations_source: fresh ? "network" : "snapshot",
      launchdarkly_identify_count: launchDarklyIdentifyCount(),
      launchdarkly_identify_skipped: launchDarklyIdentifySkipped(),
      ...bootStageTimings(),
      ...bootVisibility(),
    });
  }, [integrations, fresh, mixpanel]);

  // Only withhold children when there is genuinely nothing to render with. A
  // settled query with no data means the request failed, which stays as it was:
  // render with an empty catalogue rather than blocking the workspace forever.
  if (!integrations && loading) return null;

  return (
    <IntegrationsStaleContext.Provider value={!fresh && snapshot !== null}>
      <IntegrationContext.Provider value={integrations ?? []}>
        {children}
      </IntegrationContext.Provider>
    </IntegrationsStaleContext.Provider>
  );
};

/**
 * Issues the same query as `IntegrationsProvider`, from above `AccountValidator`
 * so it overlaps the LaunchDarkly identify that gate awaits instead of queueing
 * behind it. Both hooks share one request; the provider below renders the
 * result and handles the failure.
 */
export const IntegrationsPrefetcher = () => {
  useAvailableIntegrationsQuery();
  return null;
};

export const DestinationsIntegrationsProvider = ({
  children,
}: React.PropsWithChildren) => {
  const { data, loading } = useDestinationIntegrationsQuery();
  if (loading) return null;
  return (
    <IntegrationContext.Provider value={data?.destinationIntegrations ?? []}>
      {children}
    </IntegrationContext.Provider>
  );
};
